// Based on design @ https://www.figma.com/file/32HQdfPc2HHKNVUEy8k1HH/Human-Evals-v1?type=design&node-id=15-17795&mode=design&t=7SsyVDSM0Ox33UzS-4

import { classNames } from "@/lib/utils";
import { Float } from "@headlessui-float/react";
import { Combobox as ComboboxPrimitive } from "@headlessui/react";
import { CheckIcon, ChevronDownIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import React, { useState } from "react";
import { Checkbox } from "antd";
import Tag from "@library/v2/Tag";

const lgPadding = "!px-12 py-6";
const mdPadding = "!px-8 py-4";
const smPadding = "!px-6 py-0";
const noPadding = "!px-0";

export const COMBOBOX_DATA_TESTID_TAG = "TAG_TO_FIND_COMBOBOX_COMPONENT";

type RenderOption<T> = (option: T) => {
  // id: string;
  name: string;
  secondary?: string;
  color?: "red" | "green" | "gray" | "violet";
};

type ComboboxPropsBase<T extends { id: string; name: string }> = {
  id?: string;
  onBlur?: () => void;
  onOpenChange?: (open: boolean) => void;
  placeholder: string;
  options: T[];
  className?: string;
  wrapperClassName?: string;
  optionsClassName?: string;
  renderOption: RenderOption<T>;
  size?: "lg" | "md" | "sm" | "xs";
  by?: keyof T | ((a: T, z: T) => boolean);
  useTags?: boolean;
};

type ComboboxSingleSelectProps<T extends { id: string; name: string }> = ComboboxPropsBase<T> & {
  selected: T | null;
  onSelect: (selection: T) => void;
  showSearchIcon?: boolean;
  allowCustomValue?: boolean;
  allowCustomValueText?: string;
};

type ComboboxMultiSelectProps<T extends { id: string; name: string }> = ComboboxPropsBase<T> & {
  selected: T[];
  onSelect: (selection: T[]) => void;
  inputClassName?: string;
};

export const ComboboxSingleSelect = <T extends { id: string; name: string }>({
  id,
  selected,
  options,
  placeholder,
  onOpenChange,
  onSelect,
  renderOption,
  className,
  wrapperClassName,
  optionsClassName,
  useTags = false,
  showSearchIcon = true,
  allowCustomValue = false,
  allowCustomValueText = "Add",
  size = "lg",
}: ComboboxSingleSelectProps<T>) => {
  const renderedSelected = selected === null ? null : renderOption(selected);
  const padding =
    size === "lg"
      ? lgPadding
      : size == "md"
        ? mdPadding
        : size === "sm"
          ? smPadding
          : size === "xs"
            ? noPadding
            : lgPadding;

  return (
    <ComboboxPrimitive
      id={id}
      className={classNames("w-full", wrapperClassName)}
      as="div"
      value={selected}
      onChange={onSelect}
      multiple={false}
    >
      {({ open }) => {
        onOpenChange && onOpenChange(open);
        return (
          <div
            className={classNames("relative rounded border border-gray-200 focus-within:border-blue-700", className)}
          >
            <Float flip strategy="fixed" portal placement="bottom-start" className="pt-2" offset={4} adaptiveWidth>
              <ComboboxPrimitive.Button
                as="div"
                className={classNames("flex w-full items-center justify-between gap-6 rounded", padding)}
              >
                <div className="flex w-full items-center justify-between gap-10 overflow-hidden">
                  {renderedSelected ? (
                    <div className="font-semibold select-none truncate py-2 text-12-14">
                      {useTags ? (
                        <Tag shade={renderedSelected.color ?? "sky"} size={20}>
                          {renderedSelected.name}
                        </Tag>
                      ) : (
                        renderedSelected.name
                      )}
                    </div>
                  ) : (
                    <input
                      className="flex items-center truncate text-12-16 font-normal text-icon-base-4 text-oldgray-300 focus:outline-none focus:ring-0"
                      placeholder={placeholder}
                    />
                  )}
                  <div>
                    <ChevronDownIcon
                      className={classNames("h-12 w-12 cursor-pointer text-icon-base-1", open && "rotate-180")}
                      aria-hidden="true"
                    />
                  </div>
                </div>
              </ComboboxPrimitive.Button>

              <ComboboxSingleSelectOptions
                name={placeholder}
                options={options}
                selected={selected}
                className={optionsClassName}
                allowCustomValue={allowCustomValue}
                allowCustomValueText={allowCustomValueText}
                // @ts-ignore
                renderOption={renderOption}
                useTags={useTags}
                showSearchIcon={showSearchIcon}
              />
            </Float>
          </div>
        );
      }}
    </ComboboxPrimitive>
  );
};

interface ComboboxSingleSelectOptionProps<T extends { id: string; name: string }> {
  name: string;
  options: T[];
  selected: T | null;
  allowCustomValue?: boolean;
  allowCustomValueText?: string;
  renderOption: RenderOption<T>;
  useTags?: boolean;
  className?: string;
  showSearchIcon?: boolean;
}

const ComboboxSingleSelectOptions = React.forwardRef(
  <T extends { id: string; name: string }>(
    {
      name,
      options,
      selected,
      renderOption,
      allowCustomValue,
      allowCustomValueText,
      useTags,
      showSearchIcon,
      className,
    }: ComboboxSingleSelectOptionProps<T>,
    ref: React.Ref<HTMLUListElement>,
  ) => {
    const [search, setSearch] = useState<string>("");

    const filteredOptions = options.filter((option) => {
      if (search === "") {
        return true;
      }
      return renderOption(option).name.toLowerCase().includes(search.toLowerCase());
    });

    // Add current search as an option if there's no exact match and search isn't empty
    if (
      allowCustomValue &&
      search !== "" &&
      !filteredOptions.some((option) => renderOption(option).name.toLowerCase() === search.toLowerCase())
    ) {
      filteredOptions.push({
        id: search,
      } as T);
    }

    return (
      <ComboboxPrimitive.Options
        ref={ref}
        className={classNames(
          "z-10 mt-6 min-w-192 overflow-hidden rounded-ms bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 ui-open:animate-slide-down-fade  focus:outline-none",
          className,
        )}
      >
        <div className="max-h-[232px] overflow-auto scrollbar-none">
          <div className="group relative flex grow items-center border-b border-b-stroke-base-2 px-8 text-gray-700 outline-none focus:outline-none">
            {showSearchIcon && <SearchIcon className="h-16 w-16" />}
            <ComboboxPrimitive.Input
              autoFocus
              className="text-base-4 w-full border-none p-8 pr-28 text-12-14 placeholder-gray-500 focus:outline-none focus:ring-0"
              placeholder={`Search...`}
              onChange={(event) => setSearch(event.target.value)}
              value={search}
              /* @ts-ignore */
              onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Escape") {
                  setSearch("");
                  if (search !== "") {
                    // Prevents the dropdown from closing
                    event.preventDefault();
                  }
                }
              }}
            />
            {allowCustomValue && (
              <ComboboxPrimitive.Options>
                {search.length > 0 && (
                  <ComboboxPrimitive.Option value={{ id: search, name: search }}></ComboboxPrimitive.Option>
                )}
              </ComboboxPrimitive.Options>
            )}
          </div>
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.map((option) => {
                const { name, secondary, color } = renderOption(option);
                return (
                  <ComboboxPrimitive.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      classNames("relative cursor-pointer select-none py-8", active && "bg-gray-100")
                    }
                  >
                    <div className="flex w-full items-center justify-between px-10 ">
                      <div className="flex w-full items-center justify-between gap-10 overflow-hidden">
                        <div className="font-semibold truncate text-12-14">
                          {useTags ? (
                            <Tag shade={color || "sky"} size={20}>
                              {name}
                            </Tag>
                          ) : (
                            name
                          )}
                        </div>
                        {secondary && <div className="truncate text-11-12 font-light text-gray-300">{secondary}</div>}
                      </div>
                      <div>
                        {selected !== null && option.id == selected.id && (
                          <span className={classNames("relative -right-2")}>
                            <CheckIcon className="h-16 w-16" aria-hidden="true" />
                          </span>
                        )}
                      </div>
                    </div>
                  </ComboboxPrimitive.Option>
                );
              })}
            </>
          ) : (
            !allowCustomValue && (
              <div className="relative select-none py-8">
                <div className="flex w-full items-center justify-between px-10">
                  <div className="flex w-full items-center justify-between gap-10 overflow-hidden">
                    <div className="font-semibold font-inherit truncate text-13-16 text-text-base-4">None found</div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </ComboboxPrimitive.Options>
    );
  },
);
ComboboxSingleSelectOptions.displayName = "ComboboxSingleSelectOptions";

export const ComboboxMultiSelect = <T extends { id: string; name: string }>({
  id,
  options,
  selected,
  placeholder,
  className,
  wrapperClassName,
  optionsClassName,
  inputClassName,
  onBlur,
  onSelect,
  onOpenChange,
  renderOption,
  size = "lg",
  useTags = true,
}: ComboboxMultiSelectProps<T>) => {
  const [search, setSearch] = useState<string>("");
  const handleSelect = (value: T) => {
    // Check that the value is not already selected
    if (selected.some((s) => s.id === value.id)) {
      return;
    }
    onSelect([...selected, value]);
  };

  const padding =
    size === "lg"
      ? lgPadding
      : size == "md"
        ? mdPadding
        : size === "sm"
          ? smPadding
          : size === "xs"
            ? noPadding
            : lgPadding;

  return (
    <ComboboxPrimitive
      id={id}
      className={classNames("w-full", wrapperClassName)}
      as="div"
      value={selected}
      onBlur={onBlur}
      onChange={(value) => {
        onSelect(value);
        setSearch("");
      }}
      multiple={true}
    >
      {({ open, activeIndex }) => {
        onOpenChange && onOpenChange(open);

        return (
          <div
            className={classNames(
              "relative w-full rounded border border-gray-200 focus-within:border-blue-700",
              className,
            )}
          >
            <Float flip strategy="absolute" portal placement="bottom-start" adaptiveWidth>
              <ComboboxPrimitive.Button
                as="div"
                className={classNames("group flex items-center justify-between gap-6 rounded px-12 py-6", padding)}
              >
                <div className="thin-scrollbar flex max-h-[78px] w-full flex-wrap items-center gap-6 overflow-y-auto text-12-14 text-black">
                  {selected.length > 0 &&
                    selected.map((selectedItem) => {
                      return (
                        <div
                          key={selectedItem.id}
                          className={classNames(
                            "font-semibold flex cursor-pointer items-center gap-12 rounded px-6 py-4",
                            "color" in selectedItem && selectedItem.color === "red"
                              ? "bg-red-100 hover:bg-red-150"
                              : "color" in selectedItem && selectedItem.color === "green"
                                ? "bg-green-100 hover:bg-green-150"
                                : "bg-gray-100 hover:bg-gray-150",
                          )}
                        >
                          <div className="gap-10 overflow-hidden">
                            <div className="truncate">{selectedItem.name}</div>
                          </div>
                          <XIcon
                            className="h-12 w-12 hover:scale-90"
                            aria-hidden="true"
                            onClick={() => onSelect(selected.filter((s) => s !== selectedItem))}
                          />
                        </div>
                      );
                    })}
                  <ComboboxPrimitive.Input
                    className={classNames(
                      "w-0 min-w-0 grow border-0 px-0 py-4 text-12-14 focus:border-0 focus:ring-0",
                      inputClassName,
                    )}
                    data-testid={COMBOBOX_DATA_TESTID_TAG}
                    value={search}
                    placeholder={selected.length === 0 ? placeholder : ""}
                    onKeyDownCapture={(event) => {
                      // If the user presses backspace when the search is empty, remove the last item from the list.
                      // Otherwise allow the key press to propagate to the onChange handler.
                      if (search === "" && event.key === "Backspace") {
                        onSelect(selected.slice(0, selected.length - 1));
                        event.preventDefault();
                      }

                      // If the user presses enter, attempt to add the search term to the list, as long as it appears in the options.
                      // Otherwise allow the key press to propagate to the onChange handler.
                      if (event.key === "Enter") {
                        const matchingOption = options.find((option) => option.name === search);
                        if (matchingOption) {
                          handleSelect(matchingOption);
                          setSearch("");
                          event.preventDefault();
                        }
                      }

                      // If the user presses tab, attempt to add the 'active option' (i.e. the one highlighted in the list)
                      // to the selection.
                      if (event.key === "Tab" && !event.shiftKey) {
                        if (activeIndex !== undefined && activeIndex !== null) {
                          const matchingOption = options[activeIndex];
                          handleSelect(matchingOption);
                          setSearch("");
                          event.preventDefault();
                        }
                      }
                    }}
                    onChange={(event) => {
                      setSearch(event.target.value);
                    }}
                  />
                </div>
                <div>
                  <ChevronDownIcon
                    className={classNames("h-16 w-16 cursor-pointer text-icon-base-3", open && "rotate-180")}
                    aria-hidden="true"
                  />
                </div>
              </ComboboxPrimitive.Button>
              <ComboboxMultiSelectOptions
                className={optionsClassName}
                search={search}
                options={options}
                selected={selected}
                useTags={useTags}
                // @ts-ignore
                renderOption={renderOption}
              />
            </Float>
          </div>
        );
      }}
    </ComboboxPrimitive>
  );
};

interface ComboboxMultiSelectOptionProps<T extends { id: string }> {
  options: T[];
  search: string;
  onBlur?: () => void;
  selected: T[];
  renderOption: RenderOption<T>;
  useTags?: boolean;
  className?: string;
}

const ComboboxMultiSelectOptions = React.forwardRef(
  <T extends { id: string }>(
    { search, options, selected, renderOption, onBlur, useTags, className }: ComboboxMultiSelectOptionProps<T>,
    ref: React.Ref<HTMLUListElement>,
  ) => {
    const filteredOptions = options.filter((option) => {
      if (search === "") {
        return true;
      }
      return renderOption(option).name.toLowerCase().includes(search.toLowerCase());
    });

    return (
      <ComboboxPrimitive.Options
        ref={ref}
        className={classNames(
          "z-10 mt-4 min-w-[240px] overflow-hidden rounded-ms bg-white text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none",
          className,
        )}
      >
        <div className="max-h-[232px] overflow-auto">
          {filteredOptions.length > 0 ? (
            <>
              {filteredOptions.map((option) => {
                const { name, secondary, color } = renderOption(option);
                return (
                  <ComboboxPrimitive.Option
                    key={option.id}
                    value={option}
                    className={({ active }) =>
                      classNames("relative cursor-pointer select-none py-8", active && "bg-gray-100")
                    }
                  >
                    <div className="flex w-full items-center justify-between space-x-6 px-10">
                      <div>
                        <Checkbox
                          checked={selected.some((s) => s.id === option.id)}
                          onChange={() => {}}
                          className="relative -right-2"
                        />
                      </div>
                      <div className="flex w-full items-center justify-between gap-10 overflow-hidden">
                        <div className="font-semibold truncate text-13-16">
                          {useTags ? (
                            <Tag shade={color || "gray"} size={18}>
                              {name}
                            </Tag>
                          ) : (
                            name
                          )}
                        </div>
                        {secondary && (
                          <div className="truncate p-1 text-11-12 font-light text-gray-300">{secondary}</div>
                        )}
                      </div>
                    </div>
                  </ComboboxPrimitive.Option>
                );
              })}
            </>
          ) : (
            <div className="relative select-none py-8">
              <div className="flex w-full items-center justify-between px-10 ">
                <div className="flex w-full items-center justify-between gap-10 overflow-hidden">
                  <div className="font-semibold font-inherit truncate text-13-16 text-text-base-4">None found</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ComboboxPrimitive.Options>
    );
  },
);
ComboboxMultiSelectOptions.displayName = "ComboboxMultiSelectOptions";
